'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { trackEvent } from '@/lib/analytics';

export function LeadCaptureForm() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(true);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast.error('Introduce un email válido.');
      return;
    }
    if (!consent) {
      toast.error('Necesitamos tu consentimiento para enviarte el acceso.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'landing-final-cta' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error ?? 'Error de red');
      }
      trackEvent('Lead Submitted', { source: 'landing-final-cta' });
      toast.success(
        'Email guardado. Continúa creando tu cuenta para acceder al curso.',
      );
      router.push(`/registro?email=${encodeURIComponent(email)}`);
    } catch (err: unknown) {
      toast.error(
        err instanceof Error ? err.message : 'No pudimos registrar el email.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative isolate overflow-hidden py-20 sm:py-24">
      <div
        className="absolute inset-0 -z-10 opacity-50"
        style={{
          background:
            'radial-gradient(60% 60% at 50% 50%, rgba(201,169,97,0.18) 0%, transparent 70%)',
        }}
        aria-hidden
      />
      <div className="container-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          className="mx-auto max-w-3xl rounded-3xl border border-gold-500/30 bg-navy-900/60 p-8 text-center backdrop-blur-xl sm:p-12"
        >
          <p className="text-xs uppercase tracking-[0.22em] text-gold-300">
            Empieza ahora
          </p>
          <h2 className="heading-display mt-3 text-3xl text-cream sm:text-4xl">
            Acceso inmediato.{' '}
            <span className="text-gold-gradient">Cero euros.</span>
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-cream/70">
            Introduce tu email y te llevamos al registro. En menos de un minuto
            estarás en la primera lección.
          </p>

          <form
            onSubmit={onSubmit}
            className="mx-auto mt-8 flex max-w-xl flex-col gap-3 sm:flex-row"
          >
            <Input
              type="email"
              required
              placeholder="tu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12 flex-1"
              autoComplete="email"
              aria-label="Email"
            />
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={loading}
              className="h-12 sm:flex-shrink-0"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Acceder al curso
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </form>

          <label className="mt-4 flex cursor-pointer items-start justify-center gap-2 text-xs text-cream/55">
            <Checkbox
              checked={consent}
              onCheckedChange={(c) => setConsent(c === true)}
              className="mt-0.5"
            />
            <span className="max-w-md text-left">
              Acepto recibir comunicaciones educativas. Puedo darme de baja en
              cualquier momento.{' '}
              <a
                href="/legal/politica-privacidad"
                className="text-gold-300 underline-offset-4 hover:underline"
              >
                Política de privacidad
              </a>
              .
            </span>
          </label>

          <p className="mt-6 inline-flex items-center gap-2 text-xs text-cream/45">
            <ShieldCheck className="h-3.5 w-3.5 text-gold-400" />
            Sin spam · Sin tarjeta · Cancelas cuando quieras
          </p>
        </motion.div>
      </div>
    </section>
  );
}
